import { useEffect, useState } from "react";
import {
  Center,
  Group,
  Pagination,
  Skeleton,
  Stack,
  Table,
  Text,
  Title,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { LogoImg } from "../components/LogoImg";
import { FilterPanel } from "../components/FilterPanel";
import { useFilters } from "../hooks/useFilters";
import { api } from "../lib/api";

interface Retailer {
  retailerId: string;
  retailerName: string;
  retailerDomain: string | null;
}

interface GridCell {
  retailerId: string;
  retailerName: string;
  retailerDomain: string | null;
  currentPrice: string | null;
  listPrice: string | null;
  observedAt: string | null;
  inStock: boolean | null;
  productUrl: string | null;
}

interface GridRow {
  productId: string;
  productName: string;
  productLineId: string | null;
  productLineName: string | null;
  brandName: string;
  brandDomain: string | null;
  imageUrl: string | null;
  msrp: string | null;
  cells: GridCell[];
}

interface GridResponse {
  retailers: Retailer[];
  rows: GridRow[];
  totalProducts: number;
}

const formatCurrency = (value: string | null): string => {
  if (value === null) return "-";
  const parsed = parseFloat(value);
  if (!Number.isFinite(parsed)) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(parsed);
};

function PriceCell({
  cell,
  allPrices,
}: {
  cell: GridCell | undefined;
  allPrices: number[];
}) {
  if (!cell || cell.currentPrice === null) {
    return (
      <Text size="sm" c="dimmed" ta="center">
        -
      </Text>
    );
  }

  if (cell.inStock === false) {
    return (
      <Text size="sm" c="dimmed" ta="center">
        OOS
      </Text>
    );
  }

  const price = parseFloat(cell.currentPrice);
  const min = Math.min(...allPrices);
  const max = Math.max(...allPrices);

  let color: string | undefined;
  if (allPrices.length > 1) {
    if (price === min) color = "var(--mantine-color-green-7)";
    else if (price === max) color = "var(--mantine-color-red-7)";
  }

  return (
    <Text size="sm" fw={500} ta="center" c={color}>
      {formatCurrency(cell.currentPrice)}
    </Text>
  );
}

const PAGE_SIZE = 50;

export function GridPage() {
  const navigate = useNavigate();
  const filterCtx = useFilters();
  const [data, setData] = useState<GridResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const apiParams = filterCtx.toApiParams();
    api
      .get<GridResponse>("/grid", {
        params: { ...apiParams, page, page_size: PAGE_SIZE },
        signal: controller.signal,
      })
      .then(({ data: resp }) => {
        if (!cancelled) setData(resp);
      })
      .catch((err) => {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        notifications.show({
          title: "Failed to load grid",
          message: err instanceof Error ? err.message : "Network error",
          color: "red",
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterCtx.filterKey]);

  if (loading) {
    return (
      <Stack gap="md">
        <Title order={2}>Pricing Grid</Title>
        <Skeleton height={400} radius="sm" />
      </Stack>
    );
  }

  if (!data || data.rows.length === 0) {
    return (
      <Stack gap="md">
        <Title order={2}>Pricing Grid</Title>
        <Center py={60}>
          <Text c="dimmed">No pricing data available yet.</Text>
        </Center>
      </Stack>
    );
  }

  const totalPages = Math.ceil(data.totalProducts / PAGE_SIZE);

  return (
    <Stack gap="md">
      <Title order={2}>Pricing Grid</Title>
      <FilterPanel filters={filterCtx} showChangeTypes={false} />

      <Table.ScrollContainer minWidth={600}>
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ minWidth: 200 }}>Product</Table.Th>
              {data.retailers.map((r) => (
                <Table.Th key={r.retailerId} style={{ textAlign: "center", minWidth: 100 }}>
                  <Group gap={4} justify="center" wrap="nowrap">
                    {r.retailerDomain && (
                      <LogoImg domain={r.retailerDomain} size={16} alt={r.retailerName} />
                    )}
                    <Text size="xs" fw={600}>
                      {r.retailerName}
                    </Text>
                  </Group>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.rows.map((row) => {
              const prices = row.cells
                .filter((c) => c.currentPrice !== null && c.inStock !== false)
                .map((c) => parseFloat(c.currentPrice!));

              return (
                <Table.Tr
                  key={row.productId}
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/history/${row.productId}`)}
                >
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      {row.brandDomain && (
                        <LogoImg domain={row.brandDomain} size={20} alt={row.brandName} />
                      )}
                      <div>
                        <Text size="sm" fw={500} lineClamp={1}>
                          {row.productName}
                        </Text>
                        {row.productLineName && (
                          <Text size="xs" c="dimmed">
                            {row.productLineName}
                          </Text>
                        )}
                      </div>
                    </Group>
                  </Table.Td>
                  {data.retailers.map((retailer) => {
                    const cell = row.cells.find(
                      (c) => c.retailerId === retailer.retailerId,
                    );
                    return (
                      <Table.Td key={retailer.retailerId}>
                        <PriceCell cell={cell} allPrices={prices} />
                      </Table.Td>
                    );
                  })}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      {totalPages > 1 && (
        <Center>
          <Pagination total={totalPages} value={page} onChange={setPage} />
        </Center>
      )}
    </Stack>
  );
}
